import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router";

import { AppLayout } from "@/components/layout/app-layout";
import {
  type AgendaItem,
  type Exercise,
  getModuleStatus,
  type Module,
  MODULES,
  type Session,
} from "@/data/modules";
import { TOOLS } from "@/data/tools";
import { withLiveAgenda } from "@/lib/agenda";
import { useParticipantAgenda } from "@/lib/api";
import { cn } from "@/lib/utils";

type TabId = "before" | "during" | "after";

const TABS: { id: TabId; label: string }[] = [
  { id: "before", label: "Before" },
  { id: "during", label: "During" },
  { id: "after", label: "After" },
];

function StatusBadge({ status }: { status: ReturnType<typeof getModuleStatus> }) {
  if (status === "completed") {
    return (
      <span className="bg-muted/20 text-muted inline-block rounded-full px-3 py-1 text-xs font-medium">
        Completed
      </span>
    );
  }
  if (status === "current") {
    return (
      <span className="bg-primary inline-block rounded-full px-3 py-1 text-xs font-medium text-white">
        Current
      </span>
    );
  }
  return (
    <span className="border-border text-muted inline-block rounded-full border px-3 py-1 text-xs font-medium">
      Upcoming
    </span>
  );
}

function PrepCheckItem({
  item,
  moduleId,
  index,
}: {
  item: string;
  moduleId: number;
  index: number;
}) {
  const storageKey = `module-prep-${moduleId}-${index}`;
  const [checked, setChecked] = useState(() => localStorage.getItem(storageKey) === "true");

  const toggle = (): void => {
    const next = !checked;
    setChecked(next);
    if (next) {
      localStorage.setItem(storageKey, "true");
    } else {
      localStorage.removeItem(storageKey);
    }
  };

  return (
    <li className="flex items-start gap-3">
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-colors",
          checked ? "bg-primary border-primary" : "border-border hover:border-primary/60 bg-white",
        )}
        aria-label={checked ? "Mark as incomplete" : "Mark as complete"}
      >
        {checked && (
          <svg
            className="h-3 w-3 text-white"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      <span
        className={cn(
          "text-foreground/80 text-sm leading-relaxed",
          checked && "text-muted line-through",
        )}
      >
        {item}
      </span>
    </li>
  );
}

function BeforeTab({ module }: { module: Module }) {
  return (
    <div className="space-y-8">
      {/* Overview */}
      <section>
        <h2 className="font-heading text-foreground mb-3 text-lg font-semibold">Overview</h2>
        <p className="text-foreground/80 leading-relaxed">{module.before.overview}</p>
      </section>

      {/* Preparation checklist — hidden when empty (e.g. Module 1 has no prior work) */}
      {module.before.preparation.length > 0 && (
        <section>
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">
            Prepare for this module
          </h2>
          <ul className="space-y-3">
            {module.before.preparation.map((item, i) => (
              <PrepCheckItem key={i} item={item} moduleId={module.id} index={i} />
            ))}
          </ul>
        </section>
      )}

      {/* Carry-over */}
      {module.before.carryOver && (
        <section>
          <h2 className="font-heading text-foreground mb-3 text-lg font-semibold">
            Carry-over from previous module
          </h2>
          <div className="bg-accent-light rounded-xl p-5">
            <p className="text-accent text-sm leading-relaxed">{module.before.carryOver}</p>
          </div>
        </section>
      )}
    </div>
  );
}

/** Collect all exercises in order, flattening sections if present. */
function getAllExercises(session: Session): Exercise[] {
  if (session.sections) {
    return session.sections.flatMap((s) => s.exercises);
  }
  return session.exercises ?? [];
}

// ---------------------------------------------------------------------------
// Timer helpers (Task 2)
// ---------------------------------------------------------------------------

/** Parse a timing string to total seconds. Returns null if unparseable. */
function parseTimingToSeconds(timing: string): number | null {
  const s = timing.trim().toLowerCase();

  // "1h 30min", "1h 30 min", "1 hour 30 min", etc.
  const hoursMinutes = s.match(/^(\d+)\s*h(?:our)?s?\s+(\d+)\s*min(?:utes?)?$/);
  if (hoursMinutes) {
    return parseInt(hoursMinutes[1]!, 10) * 3600 + parseInt(hoursMinutes[2]!, 10) * 60;
  }

  // "1h", "1 hour", "2 hours"
  const hoursOnly = s.match(/^(\d+)\s*h(?:our)?s?$/);
  if (hoursOnly) {
    return parseInt(hoursOnly[1]!, 10) * 3600;
  }

  // "30 min", "30 minutes", "1 min"
  const minutesOnly = s.match(/^(\d+)\s*min(?:utes?)?$/);
  if (minutesOnly) {
    return parseInt(minutesOnly[1]!, 10) * 60;
  }

  return null;
}

function formatSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// Miro link helper (Task 3)
// ---------------------------------------------------------------------------

function renderInstructionsWithMiro(instructions: string, miroUrl: string | null): React.ReactNode {
  if (!miroUrl) {
    return instructions;
  }
  const parts = instructions.split("Miro");
  if (parts.length === 1) {
    return instructions;
  }
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <a
          href={miroUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary font-medium hover:underline"
        >
          Miro
        </a>
      )}
    </span>
  ));
}

// ---------------------------------------------------------------------------
// ExerciseItem (Tasks 1, 2, 3)
// ---------------------------------------------------------------------------

function ExerciseItem({
  exercise,
  globalIndex,
  moduleId,
  isOpen,
  onToggle,
  onComplete,
  onTimerChange,
}: {
  exercise: Exercise;
  globalIndex: number;
  moduleId: number;
  isOpen: boolean;
  onToggle: () => void;
  onComplete?: () => void;
  onTimerChange?: (info: { name: string; secondsLeft: number } | null) => void;
}) {
  const storageKey = `tesla-completed-${moduleId}-${globalIndex}`;
  const [completed, setCompleted] = useState<boolean>(() => {
    return localStorage.getItem(storageKey) === "true";
  });

  // Timer state (Task 2)
  const totalSeconds = exercise.timing ? parseTimingToSeconds(exercise.timing) : null;
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [timerRunning, setTimerRunning] = useState(false);
  const [timesUp, setTimesUp] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Miro URL (Task 3)
  const miroUrl = localStorage.getItem("tesla-miro-url") || null;

  const toggleCompleted = (e: React.MouseEvent): void => {
    e.stopPropagation();
    const next = !completed;
    setCompleted(next);
    if (next) {
      localStorage.setItem(storageKey, "true");
      onComplete?.();
    } else {
      localStorage.removeItem(storageKey);
    }
  };

  const startTimer = (): void => {
    if (totalSeconds === null) {
      return;
    }
    setTimeLeft(totalSeconds);
    setTimesUp(false);
    setTimerRunning(true);
    onTimerChange?.({ name: exercise.title, secondsLeft: totalSeconds });
  };

  const stopTimer = (): void => {
    setTimerRunning(false);
    setTimeLeft(null);
    setTimesUp(false);
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onTimerChange?.(null);
  };

  useEffect(() => {
    if (timerRunning && timeLeft !== null) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev === null || prev <= 1) {
            clearInterval(intervalRef.current!);
            intervalRef.current = null;
            setTimerRunning(false);
            setTimesUp(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerRunning]);

  // Sync running timer state to parent sticky bar
  useEffect(() => {
    if (timerRunning && timeLeft !== null) {
      onTimerChange?.({ name: exercise.title, secondsLeft: timeLeft });
    }
    if (timesUp) {
      onTimerChange?.(null);
    }
  }, [timeLeft, timerRunning, timesUp]);

  return (
    <div className="border-border rounded-lg border">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
      >
        {/* Completion checkbox (Task 1) */}
        <button
          type="button"
          onClick={toggleCompleted}
          className={cn(
            "mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors",
            completed
              ? "bg-primary border-primary"
              : "border-border hover:border-primary/60 bg-white",
          )}
          aria-label={completed ? "Mark as incomplete" : "Mark as complete"}
        >
          {completed && (
            <svg
              className="h-2.5 w-2.5 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </button>

        {/* Index circle */}
        <span className="text-primary mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold">
          {globalIndex}
        </span>

        <div className="flex flex-1 flex-col gap-1">
          <span
            className={cn(
              "text-foreground text-sm font-medium transition-colors",
              completed && "text-muted line-through",
            )}
          >
            {exercise.title}
          </span>
          {/* Exercise lengths are deliberately not shown to participants —
              facilitators keep the timings, in the Run tab and the agenda
              table. The countdown timer below still uses `exercise.timing`. */}
          {(exercise.format ?? exercise.toolIds?.length) && (
            <div className="flex flex-wrap gap-1.5">
              {exercise.format && (
                <span className="bg-accent-light text-accent rounded px-2 py-0.5 text-xs">
                  {exercise.format}
                </span>
              )}
              {exercise.toolIds?.map((toolId) => {
                const tool = TOOLS.find((t) => t.id === toolId);
                if (!tool) {
                  return null;
                }
                return (
                  <Link
                    key={toolId}
                    to={`/tools?search=${encodeURIComponent(tool.title)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors"
                  >
                    <svg
                      className="h-3 w-3 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    {tool.title}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
        <svg
          className={cn(
            "text-muted mt-0.5 h-4 w-4 flex-shrink-0 transition-transform",
            isOpen ? "rotate-180" : "",
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="border-border/50 border-t px-4 py-3">
          {/* Instructions with Miro links (Task 3) */}
          <p className="text-foreground/80 text-sm leading-relaxed whitespace-pre-line">
            {renderInstructionsWithMiro(exercise.instructions, miroUrl)}
          </p>

          {/* Timer (Task 2) */}
          {totalSeconds !== null && (
            <div className="mt-3 flex items-center gap-3">
              {!timerRunning && !timesUp && (
                <button
                  type="button"
                  onClick={startTimer}
                  className="bg-primary/10 text-primary hover:bg-primary/20 rounded px-3 py-1.5 text-xs font-medium transition-colors"
                >
                  Start timer
                </button>
              )}
              {timerRunning && timeLeft !== null && (
                <>
                  <span className="font-mono text-sm font-semibold tabular-nums">
                    {formatSeconds(timeLeft)}
                  </span>
                  <button
                    type="button"
                    onClick={stopTimer}
                    className="border-border rounded border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-red-50 hover:text-red-600"
                  >
                    Stop
                  </button>
                </>
              )}
              {timesUp && (
                <span className="text-xs font-semibold text-green-600">Time&apos;s up! ✓</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SessionAccordion({
  session,
  moduleId,
  onTimerChange,
}: {
  session: Session;
  moduleId: number;
  onTimerChange?: (info: { name: string; secondsLeft: number } | null) => void;
}) {
  const [openExercises, setOpenExercises] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    const all = getAllExercises(session);
    for (let i = 0; i < all.length; i++) {
      if (localStorage.getItem(`tesla-completed-${moduleId}-${i + 1}`) !== "true") {
        initial.add(i + 1);
        break;
      }
    }
    return initial;
  });

  const toggleExercise = (globalIndex: number): void => {
    setOpenExercises((prev) => {
      const next = new Set(prev);
      if (next.has(globalIndex)) {
        next.delete(globalIndex);
      } else {
        next.add(globalIndex);
      }
      return next;
    });
  };

  const allExercises = getAllExercises(session);

  // Build a map from exercise reference → global 1-based index
  const exerciseIndexMap = new Map<Exercise, number>();
  allExercises.forEach((ex, i) => exerciseIndexMap.set(ex, i + 1));

  return (
    <div className="border-border rounded-xl border bg-white">
      <div className="p-6">
        {/* Day header */}
        <div className="flex items-start gap-4">
          <span className="bg-primary flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
            {session.day}
          </span>
          <div className="flex-1">
            <h3 className="font-heading text-foreground font-semibold">{session.title}</h3>
            <p className="text-muted mt-2 text-sm leading-relaxed">{session.description}</p>
          </div>
        </div>

        {/* Slide deck link */}
        {session.slideUrl && (
          <div className="mt-4">
            <a
              href={session.slideUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="border-primary text-primary hover:bg-primary/5 inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors"
            >
              <svg
                className="h-4 w-4 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 5h16M4 5v10a1 1 0 001 1h14a1 1 0 001-1V5M4 5V4a1 1 0 011-1h14a1 1 0 011 1v1M12 16v4m-3 0h6"
                />
              </svg>
              Open slide deck →
            </a>
          </div>
        )}

        {/* Materials strip (Change 4) */}
        {(() => {
          const allToolIds = [...new Set(allExercises.flatMap((ex) => ex.toolIds ?? []))];
          if (allToolIds.length === 0) {
            return null;
          }
          return (
            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="text-muted text-xs font-semibold tracking-wider uppercase">
                Templates
              </span>
              {allToolIds.map((toolId) => {
                const tool = TOOLS.find((t) => t.id === toolId);
                if (!tool) {
                  return null;
                }
                return (
                  <Link
                    key={toolId}
                    to={`/tools?search=${encodeURIComponent(tool.title)}`}
                    className="bg-primary/10 text-primary hover:bg-primary/20 flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium transition-colors"
                  >
                    <svg
                      className="h-3 w-3 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                      />
                    </svg>
                    {tool.title}
                  </Link>
                );
              })}
            </div>
          );
        })()}

        {/* Exercises — flat or sections */}
        {allExercises.length > 0 && (
          <div className="border-border/50 mt-5 space-y-4 border-t pt-4">
            {session.sections ? (
              session.sections.map((section, si) => (
                <div key={si}>
                  {/* Section divider — always shown */}
                  <div className="mb-2 flex items-center gap-3">
                    <span className="text-foreground/60 text-xs font-semibold tracking-wider uppercase">
                      {section.label}
                    </span>
                    <div className="border-border/40 flex-1 border-t" />
                  </div>
                  {/* Exercises within section */}
                  {section.exercises.length > 0 && (
                    <div className="space-y-2">
                      {section.exercises.map((exercise, ei) => {
                        const globalIdx = exerciseIndexMap.get(exercise) ?? ei + 1;
                        return (
                          <ExerciseItem
                            key={ei}
                            exercise={exercise}
                            globalIndex={globalIdx}
                            moduleId={moduleId}
                            isOpen={openExercises.has(globalIdx)}
                            onToggle={() => toggleExercise(globalIdx)}
                            onComplete={() => {
                              const nextIdx = globalIdx + 1;
                              if (nextIdx <= allExercises.length) {
                                setOpenExercises((prev) => {
                                  const next = new Set(prev);
                                  next.add(nextIdx);
                                  return next;
                                });
                              }
                            }}
                            onTimerChange={onTimerChange}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="space-y-2">
                {(session.exercises ?? []).map((exercise, index) => (
                  <ExerciseItem
                    key={index}
                    exercise={exercise}
                    globalIndex={index + 1}
                    moduleId={moduleId}
                    isOpen={openExercises.has(index + 1)}
                    onToggle={() => toggleExercise(index + 1)}
                    onComplete={() => {
                      const nextIdx = index + 2;
                      if (nextIdx <= allExercises.length) {
                        setOpenExercises((prev) => {
                          const next = new Set(prev);
                          next.add(nextIdx);
                          return next;
                        });
                      }
                    }}
                    onTimerChange={onTimerChange}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AgendaList({ items }: { items: AgendaItem[] }) {
  return (
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i}>
          {item.subGroup && (
            <p className="text-muted mt-2 mb-1.5 text-xs font-semibold tracking-wide uppercase">
              {item.subGroup}
            </p>
          )}
          <div className="flex items-start gap-2 text-sm">
            <span className="text-foreground/80">{item.title}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ModuleAgendaView({ sessions }: { sessions: Session[] }) {
  const hasAgenda = sessions.every((s) => s.agenda);
  const hasSections = sessions.every((s) => s.sections && s.sections.length > 0);
  if (!hasAgenda && !hasSections) {
    return null;
  }

  return (
    <div className="border-border mb-8 overflow-hidden rounded-2xl border">
      {/* Top section: header + morning per day */}
      <div className="divide-border grid grid-cols-3 divide-x">
        {sessions.map((session, i) => (
          <div key={i} className="bg-white p-5">
            {/* Day header */}
            <div className="bg-primary mb-4 rounded-xl p-3 text-center text-white">
              <p className="font-heading text-base font-bold">
                Day {session.day}
                {session.timeRange && (
                  <span className="ml-1.5 text-sm font-normal opacity-90">
                    [{session.timeRange}]
                  </span>
                )}
              </p>
              {session.subtitle && (
                <p className="mt-0.5 text-sm font-semibold underline underline-offset-2">
                  {session.subtitle}
                </p>
              )}
            </div>
            {/* Morning */}
            {session.agenda ? (
              <AgendaList items={session.agenda.morning} />
            ) : (
              <AgendaList
                items={(session.sections!.find((s) => s.label === "Morning")?.exercises ?? []).map(
                  (ex) => ({ title: ex.title, duration: ex.timing ?? "" }),
                )}
              />
            )}
          </div>
        ))}
      </div>

      {/* Lunch bar */}
      <div className="bg-[#11262B] px-6 py-2.5 text-center text-sm font-semibold text-white">
        Lunch 🍝
      </div>

      {/* Afternoon per day */}
      <div className="divide-border grid grid-cols-3 divide-x">
        {sessions.map((session, i) => (
          <div key={i} className="bg-white p-5">
            {session.agenda ? (
              <AgendaList items={session.agenda.afternoon} />
            ) : (
              <AgendaList
                items={session
                  .sections!.filter(
                    (s) => s.label !== "Morning" && !s.label.toLowerCase().includes("lunch"),
                  )
                  .flatMap((s) =>
                    s.exercises.map((ex) => ({ title: ex.title, duration: ex.timing ?? "" })),
                  )}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile: one-day-at-a-time accordion for During tab
// ---------------------------------------------------------------------------

function MobileDayPanel({
  session,
  moduleId,
  isOpen,
  onToggle,
  onTimerChange,
}: {
  session: Session;
  moduleId: number;
  isOpen: boolean;
  onToggle: () => void;
  onTimerChange?: (info: { name: string; secondsLeft: number } | null) => void;
}) {
  const [openExercises, setOpenExercises] = useState<Set<number>>(() => {
    const initial = new Set<number>();
    const all = getAllExercises(session);
    for (let i = 0; i < all.length; i++) {
      if (localStorage.getItem(`tesla-completed-${moduleId}-${i + 1}`) !== "true") {
        initial.add(i + 1);
        break;
      }
    }
    return initial;
  });
  const allExercises = getAllExercises(session);
  const exerciseIndexMap = new Map<Exercise, number>();
  allExercises.forEach((ex, i) => exerciseIndexMap.set(ex, i + 1));

  const toggleExercise = (idx: number): void => {
    setOpenExercises((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  return (
    <div className="border-border overflow-hidden rounded-xl border bg-white">
      {/* Day toggle header */}
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-4 py-4"
      >
        <div className="flex items-center gap-3">
          <span className="bg-primary flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
            {session.day}
          </span>
          <div className="text-left">
            <p className="text-foreground text-sm font-semibold">{session.title}</p>
            {session.timeRange && <p className="text-muted text-xs">{session.timeRange}</p>}
          </div>
        </div>
        <svg
          className={cn(
            "text-muted h-4 w-4 flex-shrink-0 transition-transform",
            isOpen && "rotate-180",
          )}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="border-border border-t">
          {/* Session description + subtitle */}
          {(session.description ?? session.subtitle) && (
            <div className="px-4 pt-4 pb-1">
              {session.subtitle && (
                <p className="text-primary mb-1 text-xs font-semibold tracking-wide uppercase">
                  {session.subtitle}
                </p>
              )}
              {session.description && (
                <p className="text-muted text-sm leading-relaxed">{session.description}</p>
              )}
              {session.slideUrl && (
                <a
                  href={session.slideUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-primary text-primary hover:bg-primary/5 mt-3 inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors"
                >
                  <svg
                    className="h-4 w-4 flex-shrink-0"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 5h16M4 5v10a1 1 0 001 1h14a1 1 0 001-1V5M4 5V4a1 1 0 011-1h14a1 1 0 011 1v1M12 16v4m-3 0h6"
                    />
                  </svg>
                  Open slide deck →
                </a>
              )}
            </div>
          )}

          {/* Agenda — morning / lunch / afternoon split when available, flat list otherwise */}
          {allExercises.length > 0 && (
            <div className="bg-muted/10 mx-4 my-3 rounded-lg px-4 py-3">
              <p className="text-muted mb-2 text-xs font-semibold tracking-wider uppercase">
                Day agenda
              </p>
              {session.agenda ? (
                <>
                  <ol className="space-y-1.5">
                    {session.agenda.morning.map((item, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-xs">
                        {item.subGroup && (
                          <span className="text-muted mr-1 font-semibold uppercase">
                            {item.subGroup}
                          </span>
                        )}
                        <span className="text-foreground/80 flex-1">{item.title}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="my-2 rounded bg-[#11262B] px-3 py-1 text-center text-xs font-semibold text-white">
                    Lunch
                  </div>
                  <ol className="space-y-1.5">
                    {session.agenda.afternoon.map((item, i) => (
                      <li key={i} className="flex items-baseline gap-2 text-xs">
                        {item.subGroup && (
                          <span className="text-muted mr-1 font-semibold uppercase">
                            {item.subGroup}
                          </span>
                        )}
                        <span className="text-foreground/80 flex-1">{item.title}</span>
                      </li>
                    ))}
                  </ol>
                </>
              ) : (
                <ol className="space-y-1.5">
                  {allExercises.map((ex, i) => {
                    const idx = i + 1;
                    const done =
                      localStorage.getItem(`tesla-completed-${moduleId}-${idx}`) === "true";
                    return (
                      <li key={i} className="flex items-baseline justify-between gap-2 text-xs">
                        <span
                          className={cn("text-foreground/80", done && "text-muted line-through")}
                        >
                          {idx}. {ex.title}
                        </span>
                        {done && <span className="text-muted flex-shrink-0">✓</span>}
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          )}

          {/* Exercise items */}
          {allExercises.length > 0 && (
            <div className="space-y-2 p-4">
              {session.sections
                ? session.sections.map((section, si) => (
                    <div key={si}>
                      {section.exercises.length > 0 && (
                        <>
                          <div className="mb-2 flex items-center gap-3">
                            <span className="text-foreground/60 text-xs font-semibold tracking-wider uppercase">
                              {section.label}
                            </span>
                            <div className="border-border/40 flex-1 border-t" />
                          </div>
                          <div className="space-y-2">
                            {section.exercises.map((exercise, ei) => {
                              const globalIdx = exerciseIndexMap.get(exercise) ?? ei + 1;
                              return (
                                <ExerciseItem
                                  key={ei}
                                  exercise={exercise}
                                  globalIndex={globalIdx}
                                  moduleId={moduleId}
                                  isOpen={openExercises.has(globalIdx)}
                                  onToggle={() => toggleExercise(globalIdx)}
                                  onComplete={() => {
                                    const nextIdx = globalIdx + 1;
                                    if (nextIdx <= allExercises.length) {
                                      setOpenExercises((prev) => {
                                        const next = new Set(prev);
                                        next.add(nextIdx);
                                        return next;
                                      });
                                    }
                                  }}
                                  onTimerChange={onTimerChange}
                                />
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                : (session.exercises ?? []).map((exercise, idx) => (
                    <ExerciseItem
                      key={idx}
                      exercise={exercise}
                      globalIndex={idx + 1}
                      moduleId={moduleId}
                      isOpen={openExercises.has(idx + 1)}
                      onToggle={() => toggleExercise(idx + 1)}
                      onComplete={() => {
                        const nextIdx = idx + 2;
                        if (nextIdx <= allExercises.length) {
                          setOpenExercises((prev) => {
                            const next = new Set(prev);
                            next.add(nextIdx);
                            return next;
                          });
                        }
                      }}
                      onTimerChange={onTimerChange}
                    />
                  ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MobileDayAccordions({
  module,
  onTimerChange,
}: {
  module: Module;
  onTimerChange?: (info: { name: string; secondsLeft: number } | null) => void;
}) {
  const [openDay, setOpenDay] = useState<number>(module.during.sessions[0]?.day ?? 1);

  return (
    <div className="space-y-2">
      {module.during.sessions.map((session) => (
        <MobileDayPanel
          key={session.day}
          session={session}
          moduleId={module.id}
          isOpen={openDay === session.day}
          onToggle={() => setOpenDay(openDay === session.day ? 0 : session.day)}
          onTimerChange={onTimerChange}
        />
      ))}
    </div>
  );
}

function getDefaultActiveDay(module: Module): number {
  for (const session of module.during.sessions) {
    const all = getAllExercises(session);
    for (let i = 0; i < all.length; i++) {
      if (localStorage.getItem(`tesla-completed-${module.id}-${i + 1}`) !== "true") {
        return session.day;
      }
    }
  }
  return module.during.sessions[0]?.day ?? 1;
}

function DuringTab({ module }: { module: Module }) {
  const [activeDay, setActiveDay] = useState(() => getDefaultActiveDay(module));
  const [runningTimer, setRunningTimer] = useState<{
    name: string;
    secondsLeft: number;
  } | null>(null);

  const handleTimerChange = useCallback((info: { name: string; secondsLeft: number } | null) => {
    setRunningTimer(info);
  }, []);

  return (
    <>
      <div className="space-y-8">
        {/* Desktop: visual agenda grid + stacked sessions */}
        <div className="hidden md:block">
          <ModuleAgendaView sessions={module.during.sessions} />
        </div>

        <section className="hidden md:block">
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">Sessions</h2>

          {/* Day selector tabs */}
          <div className="mb-6 flex gap-2">
            {module.during.sessions.map((session) => (
              <button
                key={session.day}
                type="button"
                onClick={() => setActiveDay(session.day)}
                className={cn(
                  "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                  activeDay === session.day
                    ? "bg-primary text-white"
                    : "bg-muted/20 text-muted hover:text-foreground",
                )}
              >
                Day {session.day}
                {session.subtitle ? ` · ${session.subtitle}` : ""}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {module.during.sessions.map((session) => (
              <div key={session.day} className={cn(activeDay !== session.day && "hidden")}>
                <SessionAccordion
                  session={session}
                  moduleId={module.id}
                  onTimerChange={handleTimerChange}
                />
              </div>
            ))}
          </div>
        </section>

        {/* Mobile: one-day-at-a-time accordion */}
        <div className="md:hidden">
          <MobileDayAccordions module={module} onTimerChange={handleTimerChange} />
        </div>

        {/* Prints & Templates */}
        {module.during.prints.length > 0 && (
          <section>
            <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">
              Prints &amp; Templates
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {module.during.prints.map((print, i) => (
                <div key={i} className="border-border rounded-xl border bg-white p-4">
                  <p className="text-foreground font-medium">{print.title}</p>
                  <p className="text-muted mt-1 text-sm leading-relaxed">{print.description}</p>
                  {print.url && (
                    <a
                      href={print.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary mt-2 inline-block text-sm font-medium hover:underline"
                    >
                      Open in Drive →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* Sticky running timer bar */}
      {runningTimer !== null && (
        <div className="fixed right-0 bottom-0 left-0 z-50 flex items-center justify-between bg-[#11262B] px-6 py-3 text-white shadow-lg">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold tracking-wider uppercase opacity-70">
              Now running
            </span>
            <span className="text-sm font-medium">{runningTimer.name}</span>
          </div>
          <span className="font-mono text-lg font-bold tabular-nums">
            {String(Math.floor(runningTimer.secondsLeft / 60)).padStart(2, "0")}:
            {String(runningTimer.secondsLeft % 60).padStart(2, "0")}
          </span>
        </div>
      )}
    </>
  );
}

function AfterTab({ module }: { module: Module }) {
  return (
    <div className="space-y-8">
      {/* Accomplishments */}
      {module.after.accomplishments && (
        <section>
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">
            What you accomplished
          </h2>
          <div className="rounded-xl border border-green-100 bg-green-50 p-5">
            <p className="text-foreground/80 text-sm leading-relaxed">
              {module.after.accomplishments}
            </p>
          </div>
        </section>
      )}

      {/* Resources */}
      {module.after.resources.length > 0 && (
        <section>
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">Resources</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {module.after.resources.map((resource, i) => (
              <div key={i} className="border-border rounded-xl border bg-white p-5">
                <p className="text-foreground font-semibold">{resource.title}</p>
                <p className="text-muted mt-2 text-sm leading-relaxed">{resource.description}</p>
                {resource.url && (
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary mt-2 inline-block text-sm font-medium hover:underline"
                  >
                    Open →
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Next module preview */}
      {module.after.nextModulePreview && (
        <section>
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">Coming up</h2>
          <div className="bg-primary/5 border-primary/20 rounded-xl border p-5">
            <p className="text-foreground/80 line-clamp-3 text-sm leading-relaxed">
              {module.after.nextModulePreview}
            </p>
            <Link
              to={`/modules/${module.id + 1}`}
              className="text-primary mt-3 inline-block text-sm font-medium hover:underline"
            >
              Preview Module {module.id + 1} →
            </Link>
          </div>
        </section>
      )}

      {/* Between-module work — moved last, renamed, interactive checkboxes */}
      {module.after.betweenModuleWork.length > 0 && (
        <section>
          <h2 className="font-heading text-foreground mb-4 text-lg font-semibold">
            Before the next module
          </h2>
          <ul className="space-y-3">
            {module.after.betweenModuleWork.map((item, i) => (
              <PrepCheckItem key={i} item={item} moduleId={module.id + 1} index={i} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

export function ModuleCompanionPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabId>("before");

  const moduleId = id ? parseInt(id, 10) : null;
  const baseModule = MODULES.find((m) => m.id === moduleId);

  // The facilitator agenda is the source of truth for the day plan. Overlay it
  // here, once, so every view below (agenda grid, day tabs, mobile accordion)
  // renders the same thing without knowing where the agenda came from.
  const { data: liveAgendas } = useParticipantAgenda();
  const module = useMemo(
    () =>
      baseModule ? withLiveAgenda(baseModule, liveAgendas?.[String(baseModule.id)]) : undefined,
    [baseModule, liveAgendas],
  );

  if (!module) {
    return (
      <AppLayout>
        <div className="flex min-h-[60vh] items-center justify-center px-6">
          <div className="text-center">
            <p className="text-primary mb-2 text-sm font-semibold tracking-wider uppercase">404</p>
            <h1 className="font-heading text-foreground text-2xl font-bold">Module not found</h1>
            <p className="text-muted mt-2">
              That module doesn&apos;t exist.{" "}
              <Link to="/program" className="text-primary hover:underline">
                Back to program overview
              </Link>
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  const prevModule = module.id > 1 ? MODULES.find((m) => m.id === module.id - 1) : null;
  const nextModule = module.id < 4 ? MODULES.find((m) => m.id === module.id + 1) : null;
  const status = getModuleStatus(module);

  return (
    <AppLayout>
      <div className="mx-auto max-w-4xl px-6 py-10">
        {/* Module header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-start gap-4">
            <span className="bg-primary flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full font-bold text-white">
              {module.id}
            </span>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-heading text-foreground text-2xl font-bold">{module.title}</h1>
                <span className="text-muted text-sm">
                  {module.location} · {module.month}
                </span>
              </div>
              <div className="mt-2 flex items-center gap-3">
                {status === "completed" && (
                  <span className="bg-muted/20 text-muted inline-block rounded-full px-3 py-1 text-xs font-medium">
                    Completed
                  </span>
                )}
                {status === "current" && (
                  <span className="bg-primary inline-block rounded-full px-3 py-1 text-xs font-medium text-white">
                    Current
                  </span>
                )}
                {status === "upcoming" && (
                  <span className="border-border text-muted inline-block rounded-full border px-3 py-1 text-xs font-medium">
                    Upcoming
                  </span>
                )}
                <span className="text-muted text-sm italic">{module.outcome}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Welcome message (Module 1 only) */}
        {module.welcomeMessage && (
          <div className="bg-primary/5 border-primary/20 mb-8 rounded-2xl border p-6">
            <p className="text-primary mb-3 text-xs font-semibold tracking-wider uppercase">
              Welcome to the Tesla Program
            </p>
            {module.welcomeMessage.split("\n\n").map((para, i) => (
              <p key={i} className="text-foreground/80 mt-2 text-sm leading-relaxed">
                {para}
              </p>
            ))}
          </div>
        )}

        {/* Tabs */}
        <div className="border-border mb-8 border-b">
          <div className="flex gap-6">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "-mb-px pb-3 text-sm font-medium transition-colors",
                  activeTab === tab.id
                    ? "text-primary border-primary border-b-2"
                    : "text-muted hover:text-foreground",
                )}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content */}
        <div>
          {activeTab === "before" && <BeforeTab module={module} />}
          {activeTab === "during" && <DuringTab module={module} />}
          {activeTab === "after" && <AfterTab module={module} />}
        </div>

        {/* Bottom navigation */}
        <div className="mt-12 flex items-center justify-between">
          {prevModule ? (
            <Link
              to={`/modules/${prevModule.id}`}
              className="border-border text-foreground hover:border-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium transition-colors"
            >
              ← Module {prevModule.id}: {prevModule.title}
            </Link>
          ) : (
            <div />
          )}
          {nextModule ? (
            <Link
              to={`/modules/${nextModule.id}`}
              className="border-border text-foreground hover:border-primary flex items-center gap-2 rounded-lg border bg-white px-4 py-2 text-sm font-medium transition-colors"
            >
              Module {nextModule.id}: {nextModule.title} →
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// Re-export StatusBadge so it can be used elsewhere if needed
export { StatusBadge };
